import React from 'react';
import './CashInsightsCharts.css';

function max1(...xs) {
  const m = Math.max(0, ...xs.map((x) => Number(x) || 0));
  return m > 0 ? m : 1;
}

/** Horizontal comparison: cash-in vs cash-out per row (amount scale). */
export function ChannelVolumeBars({ rows, maxVolume, labelFn }) {
  const mv = maxVolume > 0 ? maxVolume : max1(...rows.map((r) => r.cashIn + r.cashOut));
  return (
    <div className="cash-viz cash-viz--volume" role="list">
      {rows.length === 0 ? (
        <p className="cash-viz-empty">No transactions for this day.</p>
      ) : (
        rows.map((r) => {
          const total = r.cashIn + r.cashOut;
          const inPct = (r.cashIn / mv) * 100;
          const outPct = (r.cashOut / mv) * 100;
          return (
            <div key={r.key} className="cash-viz-row" role="listitem">
              <div className="cash-viz-row-label" title={r.key}>
                <span className="cash-viz-name">{labelFn(r.key)}</span>
                <span className="cash-viz-meta">
                  {r.txnCount} txn · TZS {total.toLocaleString()}
                </span>
              </div>
              <div className="cash-viz-stack" aria-hidden>
                <div className="cash-viz-track">
                  <div className="cash-viz-fill cash-viz-fill--in" style={{ width: `${Math.min(100, inPct)}%` }} />
                </div>
                <div className="cash-viz-track">
                  <div className="cash-viz-fill cash-viz-fill--out" style={{ width: `${Math.min(100, outPct)}%` }} />
                </div>
              </div>
              <div className="cash-viz-nums">
                <span className="cash-viz-num cash-viz-num--in">+{Number(r.cashIn).toLocaleString()}</span>
                <span className="cash-viz-num cash-viz-num--out">−{Number(r.cashOut).toLocaleString()}</span>
              </div>
            </div>
          );
        })
      )}
      {rows.length > 0 && (
        <div className="cash-viz-legend">
          <span><i className="cash-viz-dot cash-viz-dot--in" /> Cash-in</span>
          <span><i className="cash-viz-dot cash-viz-dot--out" /> Cash-out</span>
        </div>
      )}
    </div>
  );
}

/** Popularity = number of transactions (approx. “wateja wengi”). */
export function ChannelPopularityBars({ rows, labelFn }) {
  const mv = max1(...rows.map((r) => r.txnCount));
  return (
    <div className="cash-viz cash-viz--pop" role="list">
      {rows.length === 0 ? (
        <p className="cash-viz-empty">—</p>
      ) : (
        rows.map((r) => (
          <div key={r.key} className="cash-viz-pop-row" role="listitem">
            <span className="cash-viz-pop-label">{labelFn(r.key)}</span>
            <div className="cash-viz-pop-track">
              <div
                className="cash-viz-pop-fill"
                style={{ width: `${(r.txnCount / mv) * 100}%` }}
                title={`${r.txnCount} transactions`}
              />
            </div>
            <span className="cash-viz-pop-count">{r.txnCount}</span>
          </div>
        ))
      )}
    </div>
  );
}

/**
 * Donut chart: cash-in vs cash-out for one network line on one day.
 * Muted colours (not loud): sage / dusty rose.
 */
export function CashInOutPieChart({ cashIn, cashOut, lineLabel, dateLabel, txnCount = 0 }) {
  const inAmt = Math.max(0, Number(cashIn) || 0);
  const outAmt = Math.max(0, Number(cashOut) || 0);
  const total = inAmt + outAmt;
  const inPct = total > 0 ? Math.round((inAmt / total) * 1000) / 10 : 0;
  const outPct = total > 0 ? Math.round((outAmt / total) * 1000) / 10 : 0;
  const inTurn = total > 0 ? inAmt / total : 0;

  let conic = 'conic-gradient(#cbd5e1 0turn 1turn)';
  if (total > 0) {
    if (outAmt <= 0) {
      conic = 'conic-gradient(#1a9d5c 0turn 1turn)';
    } else if (inAmt <= 0) {
      conic = 'conic-gradient(#d63b3b 0turn 1turn)';
    } else {
      conic = `conic-gradient(#1a9d5c 0turn ${inTurn}turn, #d63b3b ${inTurn}turn 1turn)`;
    }
  }

  const net = inAmt - outAmt;

  return (
    <div
      className="cash-pie-wrap"
      role="figure"
      aria-label={`Cash-in versus cash-out for ${lineLabel} on ${dateLabel}`}
    >
      <div className="cash-pie-ring-wrap">
        <div className="cash-pie-ring" style={{ background: conic }} aria-hidden />
        <div className="cash-pie-hole" aria-hidden />
      </div>
      <div className="cash-pie-meta">
        <p className="cash-pie-title">
          <strong>{lineLabel}</strong>
          <span className="cash-pie-date">{dateLabel}</span>
        </p>
        {total <= 0 ? (
          <p className="cash-pie-empty">No cash-in or cash-out on this line for this day.</p>
        ) : (
          <ul className="cash-pie-legend-list">
            <li>
              <span className="cash-pie-swatch cash-pie-swatch--in" /> Cash-in:{' '}
              <strong>TZS {inAmt.toLocaleString()}</strong> ({inPct}%)
            </li>
            <li>
              <span className="cash-pie-swatch cash-pie-swatch--out" /> Cash-out:{' '}
              <strong>TZS {outAmt.toLocaleString()}</strong> ({outPct}%)
            </li>
            <li className="cash-pie-net-line">
              Net (in − out):{' '}
              <strong className={net >= 0 ? 'cash-pie-net--pos' : 'cash-pie-net--neg'}>
                {net >= 0 ? '+' : ''}
                {net.toLocaleString()} TZS
              </strong>
              {txnCount > 0 ? <span className="cash-pie-txn"> · {txnCount} transaction{txnCount === 1 ? '' : 's'}</span> : null}
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}
